import { loadChatHistory } from "./controllers/chat";
import { loadDevices } from "./controllers/devices";
import { loadNodes } from "./controllers/nodes";
import { loadAgents } from "./controllers/agents";
import type { AuthPromptReason } from "./auth-prompt";
import { isAuthCloseEvent, resolveIsSecureContext } from "./auth-prompt";
import type { GatewayEventFrame, GatewayHelloOk } from "./gateway";
import { GatewayBrowserClient } from "./gateway";
import type { EventLogEntry } from "./app-events";
import type { AgentsListResult, PresenceEntry, HealthSnapshot, StatusSummary } from "./types";
import type { Tab } from "./navigation";
import type { UiSettings } from "./storage";
import { handleAgentEvent, resetToolStream, type AgentEventPayload } from "./app-tool-stream";
import { flushChatQueueForEvent, handleSendChat } from "./app-chat";
import {
  applySettings,
  loadCron,
  refreshActiveTab,
  setLastActiveSessionKey,
} from "./app-settings";
import { handleChatEvent, type ChatEventPayload } from "./controllers/chat";
import {
  addExecApproval,
  parseExecApprovalRequested,
  parseExecApprovalResolved,
  removeExecApproval,
} from "./controllers/exec-approval";
import {
  parseMcpSetupRequested,
  addMcpSetupRequest,
  removeMcpSetupRequest,
} from "./controllers/mcp-setup";
import type { OpenClawApp } from "./app";
import type { ExecApprovalRequest } from "./controllers/exec-approval";
import { loadAssistantIdentity } from "./controllers/assistant-identity";
import { loadSessions } from "./controllers/sessions";
import { createVoiceController } from "./controllers/voice";

type GatewayHost = {
  settings: UiSettings;
  password: string;
  client: GatewayBrowserClient | null;
  connected: boolean;
  reconnecting: boolean;
  reconnectErrorTimer: ReturnType<typeof setTimeout> | null;
  hello: GatewayHelloOk | null;
  lastError: string | null;
  showTokenPrompt: boolean;
  tokenPromptReason: AuthPromptReason | null;
  onboarding?: boolean;
  eventLogBuffer: EventLogEntry[];
  eventLog: EventLogEntry[];
  tab: Tab;
  presenceEntries: PresenceEntry[];
  presenceError: string | null;
  presenceStatus: StatusSummary | null;
  agentsLoading: boolean;
  agentsList: AgentsListResult | null;
  agentsError: string | null;
  debugHealth: HealthSnapshot | null;
  assistantName: string;
  assistantAvatar: string | null;
  assistantAgentId: string | null;
  sessionKey: string;
  chatRunId: string | null;
  refreshSessionsAfterChat: boolean;
  execApprovalQueue: ExecApprovalRequest[];
  execApprovalError: string | null;
};

type SessionDefaultsSnapshot = {
  defaultAgentId?: string;
  mainKey?: string;
  mainSessionKey?: string;
  scope?: string;
};

function normalizeSessionKeyForDefaults(
  value: string | undefined,
  defaults: SessionDefaultsSnapshot,
): string {
  const raw = (value ?? "").trim();
  const mainSessionKey = defaults.mainSessionKey?.trim();
  if (!mainSessionKey) return raw;
  if (!raw) return mainSessionKey;
  const mainKey = defaults.mainKey?.trim() || "main";
  const defaultAgentId = defaults.defaultAgentId?.trim();
  const isAlias =
    raw === "main" ||
    raw === mainKey ||
    (defaultAgentId &&
      (raw === `agent:${defaultAgentId}:main` ||
        raw === `agent:${defaultAgentId}:${mainKey}`));
  return isAlias ? mainSessionKey : raw;
}

function applySessionDefaults(host: GatewayHost, defaults?: SessionDefaultsSnapshot) {
  if (!defaults?.mainSessionKey) return;
  const resolvedSessionKey = normalizeSessionKeyForDefaults(host.sessionKey, defaults);
  const resolvedSettingsSessionKey = normalizeSessionKeyForDefaults(
    host.settings.sessionKey,
    defaults,
  );
  const resolvedLastActiveSessionKey = normalizeSessionKeyForDefaults(
    host.settings.lastActiveSessionKey,
    defaults,
  );
  const nextSessionKey = resolvedSessionKey || resolvedSettingsSessionKey || host.sessionKey;
  const nextSettings = {
    ...host.settings,
    sessionKey: resolvedSettingsSessionKey || nextSessionKey,
    lastActiveSessionKey: resolvedLastActiveSessionKey || nextSessionKey,
  };
  const shouldUpdateSettings =
    nextSettings.sessionKey !== host.settings.sessionKey ||
    nextSettings.lastActiveSessionKey !== host.settings.lastActiveSessionKey;
  if (nextSessionKey !== host.sessionKey) {
    host.sessionKey = nextSessionKey;
  }
  if (shouldUpdateSettings) {
    applySettings(host as unknown as Parameters<typeof applySettings>[0], nextSettings);
  }
}

function resolveSontaraMode(): { wsUrl: string; token: string } | null {
  const hash = window.location.hash;
  const tokenMatch = hash.match(/[#&]token=([^&]+)/);
  if (!tokenMatch) return null;
  const token = decodeURIComponent(tokenMatch[1]);
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const wsUrl = `${protocol}//${window.location.host}/ws`;
  return { wsUrl, token };
}

export function connectGateway(host: GatewayHost) {
  const sontara = resolveSontaraMode();
  if (sontara) {
    host.settings = { ...host.settings, gatewayUrl: sontara.wsUrl, token: sontara.token };
  }

  host.lastError = null;
  host.hello = null;
  host.connected = false;
  host.execApprovalQueue = [];
  host.execApprovalError = null;

  host.client?.stop();
  // Ensure WebSocket URL includes /ws path (ZeroClaw serves WS at /ws)
  let wsUrl = host.settings.gatewayUrl;
  try {
    const u = new URL(wsUrl);
    if (!u.pathname || u.pathname === "/") u.pathname = "/ws";
    wsUrl = u.toString();
  } catch { /* keep as-is if URL parsing fails */ }
  host.client = new GatewayBrowserClient({
    url: wsUrl,
    token: host.settings.token.trim() ? host.settings.token : undefined,
    password: host.password.trim() ? host.password : undefined,
    clientName: "openclaw-control-ui",
    mode: "webchat",
    onHello: (hello) => {
      host.connected = true;
      host.reconnecting = false;
      if (host.reconnectErrorTimer !== null) {
        clearTimeout(host.reconnectErrorTimer);
        host.reconnectErrorTimer = null;
      }
      host.lastError = null;
      host.hello = hello;
      applySnapshot(host, hello);
      // Reset orphaned chat run state from before disconnect.
      // Any in-flight run's final event was lost during the disconnect window.
      host.chatRunId = null;
      (host as unknown as { chatStream: string | null }).chatStream = null;
      (host as unknown as { chatStreamStartedAt: number | null }).chatStreamStartedAt = null;
      resetToolStream(host as unknown as Parameters<typeof resetToolStream>[0]);
      void loadAssistantIdentity(host as unknown as OpenClawApp);
      void loadAgents(host as unknown as OpenClawApp);
      void loadNodes(host as unknown as OpenClawApp, { quiet: true });
      void loadDevices(host as unknown as OpenClawApp, { quiet: true });
      void refreshActiveTab(host as unknown as Parameters<typeof refreshActiveTab>[0]);
      if (typeof (host as unknown as OpenClawApp).loadSecurityMode === "function") {
        void (host as unknown as OpenClawApp).loadSecurityMode();
      }

      // Create voice controller (SpeechRecognition + ElevenLabs TTS — always available)
      const app = host as unknown as OpenClawApp;
      // Resolve base URL for TTS API calls (same origin for ZeroClaw)
      const wsUrl = host.settings.gatewayUrl;
      let baseUrl = "";
      try {
        const u = new URL(wsUrl);
        baseUrl = `${u.protocol === "wss:" ? "https:" : "http:"}//${u.host}`;
      } catch { /* keep empty = same origin */ }
      app.voiceController = createVoiceController({
        sendChatMessage: (text) => {
          void handleSendChat(host as unknown as Parameters<typeof handleSendChat>[0], text);
        },
        onStateChange: (s) => { app.voiceState = s; },
        onTranscription: (text, isFinal) => {
          app.voiceTranscription = text;
          if (isFinal) app.voiceTranscription = "";
        },
        onError: (message) => { app.lastError = `Voice: ${message}`; },
        baseUrl,
        token: host.settings.token,
      }) as typeof app.voiceController;
    },
    onClose: ({ code, reason }) => {
      host.connected = false;
      // Code 1012 = Service Restart (expected during config saves, don't show as error)
      if (code === 1012) return;

      // Transient disconnects: 1001 (Going Away / server shutdown), 1006 (Abnormal / scale-to-zero)
      // Show soft "Reconnecting..." state; only escalate to error after 15s
      const isTransient = code === 1001 || code === 1006;
      if (isTransient) {
        host.reconnecting = true;
        host.lastError = null;
        if (host.reconnectErrorTimer === null) {
          host.reconnectErrorTimer = setTimeout(() => {
            host.reconnectErrorTimer = null;
            if (!host.connected) {
              host.reconnecting = false;
              host.lastError = `disconnected (${code}): ${reason || "agent may be waking up — retrying"}`;
            }
          }, 15_000);
        }
        return;
      }

      host.reconnecting = false;
      host.lastError = `disconnected (${code}): ${reason || "no reason"}`;
      if (!resolveIsSecureContext() && isAuthCloseEvent(code, reason)) {
        host.showTokenPrompt = true;
        host.tokenPromptReason = "auth-failed";
      }
    },
    onEvent: (evt) => {
      handleGatewayEvent(host, evt);
    },
    onGap: ({ expected, received }) => {
      host.lastError = `event gap detected (expected seq ${expected}, got ${received}); refresh recommended`;
    },
  });

  host.client.start();
}

export function handleGatewayEvent(host: GatewayHost, evt: GatewayEventFrame) {
  try {
    handleGatewayEventUnsafe(host, evt);
  } catch (err) {
    console.error("[gateway] handleGatewayEvent error:", evt.event, err);
  }
}

function handleGatewayEventUnsafe(host: GatewayHost, evt: GatewayEventFrame) {
  host.eventLogBuffer = [
    { ts: Date.now(), event: evt.event, payload: evt.payload },
    ...host.eventLogBuffer,
  ].slice(0, 250);
  if (host.tab === "debug") {
    host.eventLog = host.eventLogBuffer;
  }

  if (evt.event === "agent") {
    if (host.onboarding) return;
    handleAgentEvent(
      host as unknown as Parameters<typeof handleAgentEvent>[0],
      evt.payload as AgentEventPayload | undefined,
    );
    return;
  }

  if (evt.event === "chat") {
    const payload = evt.payload as ChatEventPayload | undefined;
    if (payload?.sessionKey) {
      setLastActiveSessionKey(
        host as unknown as Parameters<typeof setLastActiveSessionKey>[0],
        payload.sessionKey,
      );
    }

    // Capture chatStream text BEFORE handleChatEvent clears it on "final"
    const isFinalPayload = (payload as unknown as { state?: string })?.state === "final";
    const ttsText = isFinalPayload
      ? ((host as unknown as { chatStream?: string | null }).chatStream ?? "")
      : "";

    const state = handleChatEvent(host as unknown as OpenClawApp, payload);

    // After agent response is final, speak it via TTS if voice is active
    if (state === "final" && ttsText.trim()) {
      const app = host as unknown as OpenClawApp;
      if (app.voiceController && app.voiceState !== "idle") {
        const vc = app.voiceController as unknown as { handleAgentResponse?: (t: string) => Promise<void> };
        void vc.handleAgentResponse?.(ttsText.trim());
      }
    }

    if (state === "final" || state === "error" || state === "aborted") {
      resetToolStream(host as unknown as Parameters<typeof resetToolStream>[0]);
      void flushChatQueueForEvent(
        host as unknown as Parameters<typeof flushChatQueueForEvent>[0],
      );
      if (host.refreshSessionsAfterChat) {
        host.refreshSessionsAfterChat = false;
        if (state === "final") {
          void loadSessions(host as unknown as OpenClawApp, { activeMinutes: 0 });
        }
      }
    }
    if (state === "final") void loadChatHistory(host as unknown as OpenClawApp);
    return;
  }

  if (evt.event === "presence") {
    const payload = evt.payload as { presence?: PresenceEntry[] } | undefined;
    if (payload?.presence && Array.isArray(payload.presence)) {
      host.presenceEntries = payload.presence;
      host.presenceError = null;
      host.presenceStatus = null;
    }
    return;
  }

  // Voice events are handled by the voice controller (wired via onBinary + onEvent in app)
  if (evt.event === "voice") {
    return;
  }

  if (evt.event === "cron" && host.tab === "cron") {
    void loadCron(host as unknown as Parameters<typeof loadCron>[0]);
  }

  if (evt.event === "device.pair.requested" || evt.event === "device.pair.resolved") {
    void loadDevices(host as unknown as OpenClawApp, { quiet: true });
  }

  if (evt.event === "exec.approval.requested") {
    const entry = parseExecApprovalRequested(evt.payload);
    if (entry) {
      host.execApprovalQueue = addExecApproval(host.execApprovalQueue, entry);
      host.execApprovalError = null;
      const delay = Math.max(0, entry.expiresAtMs - Date.now() + 500);
      window.setTimeout(() => {
        host.execApprovalQueue = removeExecApproval(host.execApprovalQueue, entry.id);
      }, delay);
    }
    return;
  }

  if (evt.event === "exec.approval.resolved") {
    const resolved = parseExecApprovalResolved(evt.payload);
    if (resolved) {
      host.execApprovalQueue = removeExecApproval(host.execApprovalQueue, resolved.id);
    }
  }

  // MCP setup HITL
  if (evt.event === "mcp.setup.requested") {
    const entry = parseMcpSetupRequested(evt.payload as Record<string, unknown>);
    if (entry) {
      host.mcpSetupQueue = addMcpSetupRequest(host.mcpSetupQueue, entry);
      const delay = Math.max(0, entry.expiresAtMs - Date.now() + 500);
      window.setTimeout(() => {
        host.mcpSetupQueue = removeMcpSetupRequest(host.mcpSetupQueue, entry.id);
      }, delay);
    }
    return;
  }

  if (evt.event === "mcp.setup.resolved") {
    const payload = evt.payload as Record<string, unknown>;
    const id = payload?.id as string;
    if (id) {
      host.mcpSetupQueue = removeMcpSetupRequest(host.mcpSetupQueue, id);
    }
  }
}

export function applySnapshot(host: GatewayHost, hello: GatewayHelloOk) {
  const snapshot = hello.snapshot as
    | {
        presence?: PresenceEntry[];
        health?: HealthSnapshot;
        sessionDefaults?: SessionDefaultsSnapshot;
      }
    | undefined;
  if (snapshot?.presence && Array.isArray(snapshot.presence)) {
    host.presenceEntries = snapshot.presence;
  }
  if (snapshot?.health) {
    host.debugHealth = snapshot.health;
  }
  if (snapshot?.sessionDefaults) {
    applySessionDefaults(host, snapshot.sessionDefaults);
  }
}
